import { Plus, Eye } from "lucide-react";
import { formatUSD } from "../utils/currency";

const COMPANY_BIO = "Georgia Outdoor Worx delivers high-end craftsmanship and professional expertise to your lawn and garden. We are fully licensed and insured, providing the peace of mind and premium results your home deserves.";

export default function ProposalPreview({ form, clientName }) {
  const today = new Date().toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });
  const total = form.items.reduce((s, i) => s + (i.quantity || 1) * (i.unit_price || 0), 0);

  return (
    <div id="proposal-preview" className="bg-white text-stone-800 font-sans text-[13px] leading-relaxed shadow-lg rounded-lg overflow-hidden" style={{ width: "100%", minHeight: 700 }}>
      {/* Header — green background with white logo + title side-by-side */}
      <div className="relative bg-gradient-to-r from-teal-600 via-teal-700 to-slate-800 text-white px-8 py-10">
        <div className="absolute top-0 right-0 w-48 h-48 bg-white/5 rounded-full -translate-y-1/2 translate-x-1/4" />
        <div className="absolute bottom-0 left-20 w-32 h-32 bg-white/5 rounded-full translate-y-1/2" />
        <div className="relative z-10 flex items-center gap-6">
          {/* Logo (white silhouette on green) */}
          <img
            src="/brand/gow-logo-white-transparent.png"
            alt="Georgia Outdoor Worx"
            style={{ height: 144, width: "auto", flexShrink: 0 }}
          />
          {/* Vertical divider */}
          <div className="w-px h-28 bg-white/30" />
          {/* Title */}
          <div className="flex-1">
            <h1 className="text-3xl font-bold tracking-wider">PROPOSAL</h1>
            <div className="w-16 h-1 bg-teal-300 mt-2 mb-3" />
            <p className="text-teal-100 text-sm font-medium uppercase tracking-widest">
              {form.title || "LANDSCAPING SERVICE PROPOSAL"}
            </p>
          </div>
        </div>
      </div>

      {/* Meta Bar */}
      <div className="flex justify-between items-start px-8 py-5 border-b border-stone-200 bg-stone-50">
        <div>
          <p className="text-[10px] font-bold text-stone-400 uppercase tracking-widest mb-1">TO:</p>
          {form.company_name ? (
            <>
              <p className="font-bold text-stone-800">{form.company_name}</p>
              <p className="text-stone-500 text-xs">Attn: {clientName || "Client Name"}</p>
            </>
          ) : (
            <p className="font-bold text-stone-800">{clientName || "Client Name"}</p>
          )}
          <p className="text-stone-500 text-xs">{form.property_address || "Property Address"}</p>
        </div>
        <div className="text-right">
          <p className="text-[10px] font-bold text-stone-400 uppercase tracking-widest mb-1">Prepared by:</p>
          <p className="font-bold text-stone-800">{form.prepared_by_name || "Felipe Lima"}</p>
          <p className="text-stone-500 text-xs">Georgia Outdoor Worx</p>
          <p className="text-stone-500 text-xs">{form.prepared_by_phone || "478.418.2629"}</p>
        </div>
      </div>

      {/* Tracking + Date */}
      <div className="flex justify-between px-8 py-3 text-xs text-stone-400 border-b border-stone-100">
        <span>{form.tracking_id || "PRP-XXXX-XXX"}</span>
        <span>{today}</span>
      </div>

      {/* Project Description */}
      <div className="px-8 py-6">
        {/* Company bio (atemporal) */}
        <p className="text-stone-600 italic mb-5 leading-relaxed border-l-4 border-teal-600 pl-4">
          {COMPANY_BIO}
        </p>
        <h2 className="text-sm font-bold text-stone-800 uppercase tracking-wider border-b-2 border-teal-600 pb-1 mb-4 inline-block">
          PROJECT DESCRIPTION
        </h2>
        <p className="text-stone-600 mb-4 whitespace-pre-line">{form.project_description || "Project description will appear here..."}</p>

        {(form.proposal_services || []).map((svc, i) => (
          <div key={`svc-${i}`} className="mb-4">
            {svc.title && <p className="font-bold text-stone-800 mb-1">{svc.title}</p>}
            <ul className="list-disc pl-5 space-y-1 text-stone-600">
              {(svc.bullets || []).filter(b => b.trim()).map((b, j) => (
                <li key={`b-${i}-${j}`}>{b}</li>
              ))}
            </ul>
          </div>
        ))}
      </div>

      {/* Timeline & Cost */}
      <div className="px-8 py-6 border-t border-stone-200">
        <div className="grid grid-cols-2 gap-8">
          <div>
            <h2 className="text-sm font-bold text-stone-800 uppercase tracking-wider border-b-2 border-teal-600 pb-1 mb-3 inline-block">TIMELINE</h2>
            <p className="text-stone-600">{form.timeline || "To be determined"}</p>
          </div>
          <div>
            <h2 className="text-sm font-bold text-stone-800 uppercase tracking-wider border-b-2 border-teal-600 pb-1 mb-3 inline-block">COST</h2>
            {form.items.filter(i => i.description).map((item, idx) => (
              <div key={`cost-${idx}`} className="flex justify-between text-stone-600 mb-1">
                <span>{"\u2022"} {item.description} {item.quantity > 1 ? `(x${item.quantity})` : ""}</span>
                <span className="font-medium text-stone-800">{formatUSD(item.quantity * item.unit_price)}</span>
              </div>
            ))}
            <div className="border-t border-stone-300 mt-3 pt-2 flex justify-between font-bold text-stone-800">
              <span>TOTAL</span>
              <span>{formatUSD(total)}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Payment Terms / Notes */}
      {(form.payment_terms || form.notes) && (
        <div className="px-8 py-5 border-t border-stone-200">
          <h2 className="text-sm font-bold text-stone-800 uppercase tracking-wider border-b-2 border-teal-600 pb-1 mb-3 inline-block">OTHER NOTES</h2>
          {form.payment_terms && <p className="text-stone-600 mb-2"><strong>Payment Terms:</strong> {form.payment_terms}</p>}
          {form.notes && <p className="text-stone-600 whitespace-pre-line">{form.notes}</p>}
        </div>
      )}

      {/* Footer */}
      <div className="px-8 py-4 text-center text-xs text-stone-400 border-t border-stone-200">
        www.georgiaoutdoorworx.com
      </div>
    </div>
  );
}
